import { Color } from './java/Color';
import { Graphics2D } from './java/Graphics2D';
import { Rectangle } from './java/Geom';
import { GameObject } from './GameObject';
import { Handler } from './Handler';
import { ID } from './ID';
import { TrailPool } from './TrailPool';
import { Game } from './Game';

export class FastEnemy extends GameObject {
  private static readonly SIZE = 32;
  private static readonly FILL = new Color(78, 205, 196);
  private static readonly GLOW = new Color(78, 205, 196, 35);
  private static readonly TRAIL_COLOR = new Color(78, 205, 196);

  private handler: Handler;
  private trailTick = 0;
  private rotation = 0;
  private readonly boundsRect = new Rectangle();
  private hp = -1;

  constructor(x: number, y: number, id: ID, handler: Handler) {
    super(x, y, id);
    this.handler = handler;
    this.velX = 2;
    this.velY = 9;
  }

  setCombatHp(hp: number): void { this.hp = hp; }

  override takeDamage(dmg: number): boolean {
    if (this.hp < 0) return false;
    this.hp -= dmg;
    if (this.hp <= 0) { this.die(); return true; }
    return true;
  }

  private die(): void {
    const half = Math.trunc(FastEnemy.SIZE / 2);
    for (let i = 0; i < 6; i++) {
      const angle = Math.random() * Math.PI * 2;
      TrailPool.add(
        this.x + half + Math.cos(angle) * 8,
        this.y + half + Math.sin(angle) * 8,
        FastEnemy.FILL, half, half, 0.05, TrailPool.SHAPE_DIAMOND);
    }
    Game.addEnemyKilled();
    Game.addKillBonus(75);
    this.handler.removeObject(this);
  }

  getBounds(): Rectangle {
    this.boundsRect.x = Math.trunc(this.x);
    this.boundsRect.y = Math.trunc(this.y);
    this.boundsRect.width = FastEnemy.SIZE;
    this.boundsRect.height = FastEnemy.SIZE;
    return this.boundsRect;
  }

  tick(): void {
    const half = Math.trunc(FastEnemy.SIZE / 2);
    this.x += this.velX;
    this.y += this.velY;

    // Spin along velocity — faster movement = faster spin
    const speed = Math.sqrt(this.velX * this.velX + this.velY * this.velY);
    this.rotation += speed * 0.04;

    if (this.y <= 0 || this.y >= Game.HEIGHT - FastEnemy.SIZE) {
      Game.wallHit(this.x + half, this.y <= 0 ? 0 : Game.HEIGHT, this.y <= 0 ? 0 : 1, 78, 205, 196);
      this.velY *= -1;
    }
    if (this.x <= 0 || this.x >= Game.WIDTH - FastEnemy.SIZE) {
      Game.wallHit(this.x <= 0 ? 0 : Game.WIDTH, this.y + half, this.x <= 0 ? 2 : 3, 78, 205, 196);
      this.velX *= -1;
    }
    if (++this.trailTick % 3 === 0)
      TrailPool.add(this.x, this.y, FastEnemy.TRAIL_COLOR, FastEnemy.SIZE, FastEnemy.SIZE, 0.02, TrailPool.SHAPE_DIAMOND);
  }

  render(g: Graphics2D): void {
    const g2 = g;
    const cx = Math.trunc(this.x) + Math.trunc(FastEnemy.SIZE / 2);
    const cy = Math.trunc(this.y) + Math.trunc(FastEnemy.SIZE / 2);
    const half = Math.trunc(FastEnemy.SIZE / 2);

    // Rotated diamond
    g2.setColor(FastEnemy.GLOW);
    this.drawRotatedDiamond(g2, cx, cy, half + 4, this.rotation);
    g2.setColor(FastEnemy.FILL);
    this.drawRotatedDiamond(g2, cx, cy, half, this.rotation);
  }

  private drawRotatedDiamond(g: Graphics2D, cx: number, cy: number, r: number, angle: number): void {
    const xp: number[] = new Array<number>(4).fill(0);
    const yp: number[] = new Array<number>(4).fill(0);
    for (let i = 0; i < 4; i++) {
      const a = angle + i * (Math.PI / 2);
      xp[i] = cx + Math.trunc(Math.cos(a) * r);
      yp[i] = cy + Math.trunc(Math.sin(a) * r);
    }
    g.fillPolygon(xp, yp, 4);
  }
}
